import React, {
  useState, useCallback, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { getUserWixId } from 'modules/user/utils';
import { useAsyncAction } from 'utils/hooks/useAsyncAction';
import { noop } from 'functions/noop';
import { setSurvey, getSurvey } from 'modules/survey/api';
import { SurveyTypes } from 'modules/survey/surveyEnums';
import { SUCCESS } from 'utils/constants/queryStatuses';
import app from 'new-ui/app';
import { GenericChat } from '../GenericChat/GenericChat';
import { CHAT_ROLES } from '../constants';
import { useNbaChat } from '../useNbaChat';
import { ActionPanel } from './ActionPanel/ActionPanel';
import { NextScanChat } from './NextScanChat/NextScanChat';

const getSurveyAction = async (userWixId) => {
  const response = await getSurvey(SurveyTypes.nextScan, userWixId);
  return response?.data?.answers[0]?.value;
};

export const AssistanceChatPopup = () => {
  const {
    loading, handleSendMessage, chatHistory, spliceMessages, addMessages,
  } = useNbaChat();
  const { data: profile } = useSelector((state) => state.profile);

  const [showChatNextScan, setShowChatNextScan] = useState(false);

  const hasSponsoredTrial = !!app.trials?.filter((trial) => trial?.isSponsored)?.length;

  const userWixId = getUserWixId(profile);

  const { status: nextScanStatus, data: nextScan } = useAsyncAction({
    isActive: !!userWixId && showChatNextScan,
    action: getSurveyAction,
    parameters: userWixId,
    dataKey: `${userWixId}_nextScanSurvey`,
  });

  const onSendMessage = (...args) => {
    setShowChatNextScan(false);
    return handleSendMessage(...args);
  };

  const handleNextScanSubmit = useCallback(async ({ nextScan: nextNextScan }) => {
    const answers = [
      {
        surveyKey: 'general',
        key: 'nextScan',
        value: nextNextScan,
      },
    ];
    setSurvey(SurveyTypes.nextScan, answers, userWixId).catch(noop);

    setShowChatNextScan(false);
    // replace the last message
    spliceMessages({
      start: -1,
      deleteCount: 1,
      messages: [{
        role: CHAT_ROLES.TINA,
        content: 'Thank you for sharing with us!',
      }],
    });
  }, [spliceMessages, userWixId]);

  // must be memoized
  const chatBody = useMemo(
    () => {
      if (!showChatNextScan || nextScanStatus !== SUCCESS) {
        return null;
      }
      return (
        <NextScanChat
          nextScan={nextScan}
          onSubmit={handleNextScanSubmit}
        />
      );
    },
    [handleNextScanSubmit, nextScan, nextScanStatus, showChatNextScan],
  );

  const actionPanel = (
    <ActionPanel
      loading={loading}
      addMessages={addMessages}
      hasSponsoredTrial={hasSponsoredTrial}
      setShowChatNextScan={setShowChatNextScan}
    />
  );

  return (
    <GenericChat
      handleSendMessageClick={onSendMessage}
      actionPanel={actionPanel}
      chatHistory={chatHistory}
      chatBodyComponent={chatBody}
      loader={loading}
    />
  );
};
